import type { RelatedDictionary } from './types';
import sitesA from './ja-sites-a';
import sitesB from './ja-sites-b';

/** 関連事業(JA): 共通ラベル + 一覧(ハブ) + 詳細6本。一覧の本文も詳細と言い回しを重ねない(2026-09-18 二版)。 */
const ja: RelatedDictionary = {
  common: {
    home: 'ホーム',
    parent: '関連事業',
    official: '公式サイトを見る',
    externalNote: 'リンク先は別サイトです（別ウィンドウで開きます）',
    viewInternal: '紹介ページを読む',
    viewExternal: '公式サイト',
    otherTitle: 'ほかの関連事業も見る',
    otherSub: 'More from the group',
    backToHub: '関連事業の一覧に戻る',
    backToTop: 'ページの先頭へ',
    faqLabel: 'FAQ',
    faqTitle: 'よくある質問',
    faqSub: 'Questions and answers',
    ctaTitle: '執事を学ぶことに、興味が湧いたら',
    ctaBody: 'コースの内容、協会の認定資格、法人向け研修について、気になることは何でもお尋ねください。資料は無料でお送りします。',
    ctaCourses: 'コースを見る',
    ctaContact: 'お問い合わせ',
  },
  hub: {
    hero: {
      title: '関連事業のご案内',
      titleEn: 'RELATED ORGANIZATIONS',
      lead: '執事を育てる学びの場には、その学びを支える団体があり、学んだ執事が働く現場があり、もてなしを体験できる館があります。アカデミーを取り囲む六つの事業を、学ぶ側の視点から案内します。',
      image: { src: '/images/related/butler-association/jba-service-house-steward-team-01.jpg', alt: '協会の執事たちが並んで仕える場面（協会サイトの画像）' },
    },
    intro: {
      label: 'OUR NETWORK',
      title: '教室の外に広がる、六つの事業',
      sub: 'What surrounds the academy',
      paras: [
        'アカデミーの教室で教えていることは、教室の中だけで完結するものではありません。基準を定める協会があり、その基準で認められた執事が働くサービスがあり、執事文化を言葉で伝える人がいて、もてなしを客として味わえる館があります。学びと認定、実務、発信、体験——これらが一つの協会のもとで輪になっているからこそ、教室で覚えた所作は現場でそのまま通用します。',
      ],
      subs: [
        { t: '六つを一本の紹介ページに', b: 'この一覧では、アカデミーと関わりの深い六つの事業を、それぞれ一本の紹介ページとして用意しました。全日本執事協会／THE BUTLER／執事ベル／Queen\'s Butler／株式会社ファミリーロマンス／石井裕一オフィシャルサイト。どのページも紹介先の公式サイトの写しではなく、「執事を育てる側」から見た読み解きとして書いています。' },
        { t: '組織の線と、人の線', b: '六つのうち四つ——協会、アカデミー、THE BUTLER、Queen\'s Butler——は一般社団法人全日本執事協会のもとにあり、残る二つは協会の頭取・石井裕一という一人の人物を通じてつながっています。組織としての線と、人としての線。二本の線をたどると、日本の執事文化がどんな輪郭で成り立っているかが見えてきます。' },
      ],
      image: { src: '/images/related/butler-association/jba-organization-training-education-01.jpg', alt: '協会の研修で所作を確かめる場面（全日本執事協会の公式サイトの写真）' },
    },
    cards: {
      label: 'SIX ORGANIZATIONS',
      title: '六つの関連事業',
      sub: 'Read each introduction',
    },
    connection: {
      label: 'HOW THEY FIT TOGETHER',
      title: '六つの事業は、どう噛み合っているか',
      sub: 'The shape of the group',
      paras: [
        '中心にあるのは一般社団法人全日本執事協会です。協会が執事の基準を定め、その基準に沿ってアカデミーが教え、修了生は協会の三段階の資格で認められます。ここまでが「育てて、認める」の輪です。',
      ],
      subs: [
        { t: '認められた執事が働く場所', b: '認められた執事が実際に働くのがTHE BUTLER。家庭と法人に執事を届ける、協会直営のサービスです。講師陣はこの現場で起きた判断を教室に持ち帰るので、教科書に載らない判断まで授業に反映されます。協会の頭取・執事ベルは、執事文化の研究と発信を通じて、アカデミーが教える「なぜ」を言葉にしています。' },
        { t: '館と、二つの個人の事業', b: '同じ協会が運営するQueen\'s Butlerは、お台場の館で執事のもてなしを客として味わえる場所で、見習い体験はアカデミーの学びへの入口にもなっています。そして頭取・石井裕一が創業した株式会社ファミリーロマンスと、俳優・実業家としての歩みをまとめたオフィシャルサイト。六つの事業は役割こそ違えど、同じ人と同じ協会のもとで一つの輪をなしています。' },
      ],
      image: { src: '/images/related/queens-butler/f10-two-butlers-bow.jpg', alt: '並んで一礼する二人の執事（Queen\'s Butler の館の実写）' },
    },
    paths: {
      label: 'WHERE TO START',
      title: '目的から選ぶ、最初の一ページ',
      sub: 'Pick your entrance',
      lede: '「執事という仕事を知りたい」「執事に来てほしい」「もてなしを受けてみたい」「執事になりたい」。目的によって、最初に読むべきページは変わります。',
      items: [
        {
          title: '仕事としての執事を知りたい',
          desc: '協会の沿革と資格制度、それに執事ベルの「執事の知恵」から。執事文化の来歴と現在地がつかめます。明治の創立から法人化までの四つの章をたどると、いま教室で教えている作法がどこから来たのかが分かります。',
          image: { src: '/images/related/butler-bell/gk-16.jpg', alt: 'シルクハットを手に取る執事ベル（学研撮影・執事ベルの公式サイトの写真）' },
        },
        {
          title: '執事に来てほしい',
          desc: '協会直営のTHE BUTLERへ。家の切り盛り、経営者の右腕、記念日や式典の三領域で相談できます。名簿から執事を選べる仕組みや、相談から当日までの流れも紹介ページにまとめています。',
          image: { src: '/images/related/the-butler/b09-business-reception-doorman.jpg', alt: '受付で客を迎える執事（THE BUTLER の公式サイトの写真）' },
        },
        {
          title: 'もてなしを受けてみたい',
          desc: 'Queen\'s Butlerのサロン（女性のみ）か見習い体験へ。お台場の館で、執事の所作に直に触れられます。仕えられる側に回ってみると、教室で習う「引き方」の意味が体でわかります。',
          image: { src: '/images/related/queens-butler/s04-lounge-reading.jpg', alt: '暖炉の間でくつろぐ客に控える執事（Queen\'s Butler の館の実写）' },
        },
        {
          title: '執事になりたい',
          desc: 'アカデミーのコース案内へ。協会の三段階の資格に対応した、エッセンシャル・アドバンス・マスターの三コースを紹介しています。修了後にどの現場へ進めるかは、THE BUTLERの紹介ページが参考になります。',
          image: { src: '/images/related/butler-association/jba-mission-training-butlers-01.jpg', alt: '執事を育てる協会の研修風景（全日本執事協会の公式サイトの写真）' },
        },
      ],
    },
    faq: [
      { q: '六つの事業は全部同じ団体ですか。', a: '協会、THE BUTLER、Queen\'s Butler、アカデミーは一般社団法人全日本執事協会のもとにあります。執事ベルは協会の頭取・石井裕一の活動名とそのサイト、ファミリーロマンスと石井裕一オフィシャルサイトは、同じ石井裕一が代表・本人として関わる会社とサイトです。' },
      { q: '修了した執事はどこで働きますか。', a: '協会直営のTHE BUTLERをはじめ、家庭や法人の現場で、協会の認定を受けた執事として働きます。詳しくはTHE BUTLERの紹介ページへ。' },
      { q: '執事のもてなしを客として受けられる場所は。', a: 'Queen\'s Butlerのお台場の館で、女性のみのサロン、スタジオ、見習い体験（男女可）が利用できます。' },
      { q: 'この一覧から予約や申し込みはできますか。', a: 'できません。当サイトの紹介ページは読み物であり、依頼・予約・問い合わせは各事業の公式サイト（別ウィンドウ）で受け付けています。' },
      { q: '執事ベルと石井裕一オフィシャルサイトの違いは。', a: '前者は執事・協会の頭取としての活動、後者は俳優・実業家としての経歴をまとめたサイトです。同じ人物の二つの側面です。' },
      { q: 'アカデミーへの相談はどこから。', a: 'コース、認定資格、法人研修については当サイトのお問い合わせページからどうぞ。資料は無料です。' },
    ],
    cta: {
      title: '執事を学ぶことに、興味が湧いたら',
      body: 'コースの内容、協会の認定資格、法人向け研修について、気になることは何でもお尋ねください。資料は無料でお送りします。',
      courses: 'コースを見る',
      contact: 'お問い合わせ',
    },
    lastUpdated: '最終更新: 2026年9月18日',
  },
  sites: [...sitesA, ...sitesB],
};

export default ja;
